export type SpringOptions = {
  stiffness: number;
  damping: number;
  mass?: number;
  fps: number;
  precision: number;
}

type Ctx = {
  inv_mass: number,
  opts: SpringOptions,
  settled: boolean,
  fps: number,
  dt: number,
};

type Frames = number[]

const DEFAULTS: SpringOptions = { stiffness: 0.15, damping: 0.8, precision: 0.01, mass: 1, fps: 60 };

const springToTickValue = (frames: Frames, out = false) => (t: number) => {
  t = out ? 1 - t : t;
  const indexPrecise = t * frames.length;
  const indexExcess = indexPrecise % 1;

  const index = Math.min(~~indexPrecise, frames.length - 1);

  const a = frames[index];
  const b = frames[index + 1] == null ? a : frames[index + 1];
  return indexExcess ? a + (b - a) * indexExcess : a;
};

export function springIn(from: number, to: number, springOptions: SpringOptions) {
  return springToTransition(false, from, to, springOptions);
}

export function springOut(from: number, to: number, springOptions: SpringOptions) {
  return springToTransition(true, from, to, springOptions);
}

function springToTransition(out: boolean, from: number, to: number, opts: SpringOptions) {
  opts = Object.assign({}, DEFAULTS, opts);
  const frames = springFrames(from, to, opts)
  return {
    duration: (frames.length * 1000) / opts.fps,
    tickToValue: springToTickValue(frames, out),
  };
}

export function springFrames(from: number, to: number, opts: SpringOptions) {

  let value = from;
  let last_val = from;
  let dt = 1;
  const values = [from];

  let ctx;
  do {
    ctx = {
      inv_mass: 1 / 2,
      opts,
      settled: true,
      fps: opts.fps,
      dt,
    };

    const next_value = tick_spring(ctx, last_val, value, to);
    values.push(next_value);
    last_val = value;
    value = next_value;
  } while (!ctx.settled);

  return values;
}

function tick_spring(ctx: Ctx, last_value: number, current_value: number, target_value: number) {
  const delta = target_value - current_value;

  const velocity = (current_value - last_value) / (ctx.dt || 1 / ctx.fps); // guard div by 0
  const spring = ctx.opts.stiffness * delta;
  const damper = ctx.opts.damping * velocity;
  const acceleration = (spring - damper) * ctx.inv_mass;
  const d = (velocity + acceleration) * ctx.dt;

  if (Math.abs(d) < ctx.opts.precision && Math.abs(delta) < ctx.opts.precision) {
    return target_value; // settled
  } else {
    ctx.settled = false; // signal loop to keep ticking
    return current_value + d;
  }
}
